export function getWindowObject(): any {
  if (typeof window === "undefined") return {}
  return window
}

function getQueryString() {
  if (typeof window === "undefined") return ""
  return getWindowObject().location.search.substring(1)
}

export function getUserOSName() {
  const unknown = "-"

  const nAgt = navigator.userAgent
  let os = unknown
  const clientStrings = [
    { s: "Android", r: /Android/ },
    { s: "iOS", r: /(iPhone|iPad|iPod)/ },
    { s: "win", r: /(Windows)/ },
    {
      s: "mac",
      r: /(Mac OS X| MacPPC|MacIntel|Mac_PowerPC|Macintosh)/,
    },
    { s: "Linux", r: /(Linux|X11(?!.*CrOS))/ },
  ]

  for (const id in clientStrings) {
    const cs = clientStrings[id]
    if (cs.r.test(nAgt)) {
      os = cs.s
      break
    }
  }

  return os
}

export function getAllQueryVariables() {
  const queryVariableObject = {}
  const query = getQueryString()

  // If no query string, return empty object
  if (!query || query.trim() === '') {
    return queryVariableObject
  }

  const vars = query.split("&")
  for (let i = 0; i < vars.length; i++) {
    const pair = vars[i].split("=")
    // Only add if we have both key and value
    if (pair[0] && pair[0].trim() !== '') {
      queryVariableObject[pair[0]] = decodeURIComponent(pair[1] || '')
    }
  }

  return queryVariableObject
}

const URL_PARAMS_SESSION_KEY = 'premagic_url_params'

export function captureAndStoreUrlParameters() {
  if (typeof window === "undefined") return

  const currentParams = getAllQueryVariables()

  // Only update storage if there are new parameters, preserve existing ones
  if (Object.keys(currentParams).length > 0) {
    try {
      // Get existing stored parameters
      const existingParams = getStoredUrlParameters()

      // Merge current parameters with existing ones (current takes precedence)
      const mergedParams = { ...existingParams, ...currentParams }

      sessionStorage.setItem(URL_PARAMS_SESSION_KEY, JSON.stringify(mergedParams))
    } catch (error) {
      console.warn('Failed to store URL parameters in sessionStorage:', error)
    }
  }
  // If no current params, don't modify existing storage
}

export function getStoredUrlParameters(): Record<string, any> {
  if (typeof window === "undefined") return {}

  try {
    const storedParams = sessionStorage.getItem(URL_PARAMS_SESSION_KEY)
    return storedParams ? JSON.parse(storedParams) : {}
  } catch (error) {
    console.warn('Failed to retrieve stored URL parameters:', error)
    return {}
  }
}

export function getAllUrlParameters(): Record<string, any> {
  // First try to get from current URL
  const currentParams = getAllQueryVariables()

  // If no current params, get from sessionStorage
  if (Object.keys(currentParams).length === 0) {
    return getStoredUrlParameters()
  }

  // If we have current params, store them and return them
  captureAndStoreUrlParameters()
  return currentParams
}

export function getFormData(e): Record<string, any> {
  e.preventDefault()
  const formData = new FormData(e.currentTarget)
  return Array.from(formData.entries()).reduce(
    (memo, pair) => ({
      ...memo,
      [pair[0]]: pair[1],
    }),
    {}
  )
}

export function scrollToElement(
  element: string | HTMLElement | Element | null,
  smoothScroll?: boolean
): boolean {
  let $element
  if (typeof element === "string") {
    $element = document.querySelector(element)
  } else {
    $element = element
  }

  if (!$element) return false

  const offset = 32
  const top = $element.getBoundingClientRect().top + window.scrollY - offset

  if (smoothScroll) {
    window.scroll({
      top,
      behavior: "smooth",
    })
    return true
  }
  window.scroll(0, top)
  return true
}

export function focusOnElement(element) {
  let $element
  if (typeof element === "string") {
    $element = document.querySelector(element)
  } else {
    $element = element
  }

  if (!$element) return false
  $element.focus()
  return true
}

export function URLifyText(string: string) {
  return string.replace(/\s+/g, "-").toLowerCase()
}
